import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Toggle from "react-toggle";
import { LockIcon, Search } from "lucide-react";
import { useApolloClient, useQuery } from "@apollo/client";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { TableHeader, TableBody, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ReactSelect from "react-select";
import {
  StyledTableShell,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  EmptyState,
} from "@/components/ui/styled-table";
import { StatusBadge } from "@/components/ui/table-components";
import PublicRoleDetailModal from "./PublicRoleDetailModal";
import DemoProjects from "../DemoProjects";
import CustomPagination, {
  CustomPaginationType,
} from "../Global/CustomPagination";
import { GET_LOCALITIES, GET_PROFILE } from "../../GraphQL/settings/queries";
import {
  GET_PUBLIC_ROLES,
  GET_PARTNER_ROLES,
  GET_PROJECT_ROLES,
  GET_COMMUNITY_ROLES,
  GET_PROJECT_JOB_BOARDS,
} from "../../GraphQL/roles/queries";
import {
  PublicJob,
  PublicRolesData,
  PartnerRolesData,
  CommunityRolesData,
  LocalityData,
  ProjectJobBoards,
} from "../../interfaces";
import { currencyFormat } from "../../utils";
import {
  DesktopContainer,
  MobileContainer,
  MobileCard,
  SearchContainer,
  PageContainer,
  AdvancedSearchContainer,
  AdvancedSearchButton,
  ClearSummarySearch,
  InputSearchGroup,
  ClearSearchButton,
  ToggleIndustryWrapper,
  TextIndustryWrapper,
} from "../../sharedStyles";
import AdvancedSearch from "../AdvancedSearch";
import useCheckMobile from "../../utils/hooks/useCheckMobile";
import { useFeatureFlagEnabled } from "posthog-js/react";
import config from "../../config";

import guide from "../../assets/role_relay_user_guide.pdf";

const roleStatusLabels: Record<string, string> = {
  PLATFORM_INCLUDED: "Pending Opt-in",
  SHARED: "Shared",
  STOPPED_SHARING: "Draft",
  REMOVED: "Removed",
  UNKNOWN: "Unknown",
};

interface Props {
  user: any;
  initialActiveTab?: string;
}

const PublicRoles = ({ user, initialActiveTab = "public" }: Props) => {
  const { data: profileData, loading: profileLoading } = useQuery(GET_PROFILE);
  const shouldShowOnlyCommunityTabs = profileData?.profile?.company?.settings?.features?.communityOnly;
  const getAdvancedSearchStorage = window?.localStorage?.getItem(
    "advancedSearchRoles"
  );
  const searchStorage = getAdvancedSearchStorage
    ? JSON.parse(getAdvancedSearchStorage ?? "")
    : "";
  const client = useApolloClient();
  const navigate = useNavigate();
  const showAdvancedSearchFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showAdvancedSearch
  );
  const showDemoProjectsFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showDemoProjects
  );

  const [tabKey, setTabKey] = useState(initialActiveTab);
  const [showRoleDetailModal, setShowRoleDetailModal] = useState(false);
  const [activeRole, setActiveRole] = useState(null as any);

  const [publicRoles, setPublicRoles] = useState([] as PublicJob[]);
  const [partnerRoles, setPartnerRoles] = useState([] as PublicJob[]);
  const [communityRoles, setCommunityRoles] = useState([] as PublicJob[]);
  const [projectRoles, setProjectRoles] = useState([] as PublicJob[]);

  /**
   * Logic for deeplinked role
   */
  const [routeRoleId, setRouteRoleId] = useState<number | null>(null);
  const { roleId } = useParams<{
    roleId: string;
  }>();

  useEffect(() => {
    roleId ? setRouteRoleId(Number.parseInt(roleId)) : setRouteRoleId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (routeRoleId !== null && !showRoleDetailModal) {
    if (tabKey === "partner") {
      const role = partnerRoles.find((role) => role.id === routeRoleId);
      if (role) {
        setActiveRole(role);
        setShowRoleDetailModal(true);
      }
    } else if (tabKey === "community") {
      const role = communityRoles.find((role) => role.id === routeRoleId);
      if (role) {
        setActiveRole(role);
        setShowRoleDetailModal(true);
      }
    } else {
      const role = publicRoles.find((role) => role.id === routeRoleId);
      if (role) {
        setActiveRole(role);
        setShowRoleDetailModal(true);
      }
    }

    //TODO: handle if role not found
  }

  const [projectBoards, setProjectBoards] = useState([] as ProjectJobBoards[]);
  const [filterIndustry, setFilterIndustry] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showSummarySearchPublic, setShowSummarySearchPublic] = useState(
    searchStorage?.publicRolesStatus ?? false
  );
  const [summarySearchContentPublic, setSummarySearchContentPublic] =
    useState<string>(searchStorage?.publicRolesSummary ?? "");
  const [filterPublicQuery, setFilterPublicQuery] = useState(
    searchStorage?.publicRolesFilter ?? {}
  );
  const [showSummarySearchPartner, setShowSummarySearchPartner] = useState(
    searchStorage?.partnerRolesStatus ?? false
  );
  const [summarySearchContentPartner, setSummarySearchContentPartner] =
    useState(searchStorage?.partnerRolesSummary ?? "");
  const [filterPartnerQuery, setFilterPartnerQuery] = useState(
    searchStorage?.partnerRolesFilter ?? {}
  );
  // const [showSummarySearchProject, setShowSummarySearchProject] = useState(
  //   searchStorage?.projectRolesStatus ?? false
  // );
  // const [summarySearchContentProject, setSummarySearchContentProject] =
  //   useState(searchStorage?.projectRolesSummary ?? "");
  // const [filterProjectQuery, setFilterProjectQuery] = useState(
  //   searchStorage?.partnerRolesFilter ?? {}
  // );

  const [boardId, setBoardId] = useState<string | null>(null);
  const [boardOptions, setBoardOptions] = useState<any>(null);
  const [hasNextPagePublic, setHasNextPagePublic] = useState(false);
  const [hasNextPagePartner, setHasNextPagePartner] = useState(false);
//  const [hasNextPageProject, setHasNextPageProject] = useState(false);
  const [hasNextPageCommunity, setHasNextPageCommunity] = useState(false);

  const publicPaginationRef = useRef<CustomPaginationType>(null);
  const partnerPaginationRef = useRef<CustomPaginationType>(null);
  const projectPaginationRef = useRef<CustomPaginationType>(null);
  const communityPaginationRef = useRef<CustomPaginationType>(null);

  const { width } = useCheckMobile();
  const isMobile = width < 960;

  // Set default tab to community when communityOnly is true
  useEffect(() => {
    if (shouldShowOnlyCommunityTabs && tabKey === initialActiveTab) {
      setTabKey("community");
    }
  }, [shouldShowOnlyCommunityTabs, tabKey, initialActiveTab]);

  const { data: dataLocalities } = useQuery<LocalityData>(GET_LOCALITIES);
  const localities = dataLocalities?.localities?.items
    ? [...dataLocalities?.localities?.items]
    : [];
  const locationName = (x: string): string[] => {
    return localities.map((locality) =>
      x === locality?.name ? `${locality?.name} (${locality?.state})` : ""
    );
  };

  const { data: publicRolesData } = useQuery<PublicRolesData>(
    GET_PUBLIC_ROLES,
    {
      variables: {
        search: searchTerm,
        skip: 0,
        take: 25,
        where: filterPublicQuery,
        filterByPreferences: filterIndustry,
      },
      onCompleted: (data) => {
        setHasNextPagePublic(data?.publicJobs?.pageInfo?.hasNextPage);
        setPublicRoles(data?.publicJobs?.items);
      },
    }
  );

  const { data: partnerRolesData } = useQuery<PartnerRolesData>(
    GET_PARTNER_ROLES,
    {
      variables: {
        search: searchTerm,
        skip: 0,
        take: 25,
        where: filterPartnerQuery,
      },
      onCompleted: (data) => {
        setHasNextPagePartner(data?.partnerJobs?.pageInfo?.hasNextPage);
        setPartnerRoles(data?.partnerJobs?.items);
      },
    }
  );

  const { data: communityRolesData } = useQuery<CommunityRolesData>(
    GET_COMMUNITY_ROLES,
    {
      variables: {
        search: searchTerm,
        skip: 0,
        take: 25,
        where: filterPartnerQuery,
      },
      onCompleted: (data) => {
        setHasNextPageCommunity(data?.communityJobs?.pageInfo?.hasNextPage);
        setCommunityRoles(data?.communityJobs?.items);
      },
    }
  );

  const {
    error: errorProjectBoards,
    loading: loadingProjectBoards,
    data: dataProjectBoards,
  } = useQuery(GET_PROJECT_JOB_BOARDS);

  useEffect(() => {
    setProjectBoards(dataProjectBoards?.projectJobBoards?.items);
    if (dataProjectBoards?.projectJobBoards?.items?.length) {
      setBoardOptions(
        dataProjectBoards?.projectJobBoards?.items?.map((brd: any) => ({
          value: brd.id,
          label: brd.title,
        }))
      );

      setBoardId(dataProjectBoards?.projectJobBoards?.items[0].id);
    }
  }, [dataProjectBoards]);

  const runSearch = async (skip = 0, take = 25) => {
    if (tabKey === "public") {
      const getPublicRoles = async () => {
        const { data } = await client.query({
          query: GET_PUBLIC_ROLES,
          variables: {
            search: searchTerm,
            skip,
            take,
            where: filterPublicQuery,
            filterByPreferences: filterIndustry,
          },
        });

        setPublicRoles(data.publicJobs?.items);
        publicPaginationRef.current?.setPagination({
          ...data?.publicJobs?.pageInfo,
          skip,
          take,
          totalCount: data?.publicJobs?.totalCount,
        });
      };
      getPublicRoles();
    } else if (tabKey === "partner") {
      // partner
      const getPartnerRoles = async () => {
        const { data } = await client.query({
          query: GET_PARTNER_ROLES,
          variables: {
            search: searchTerm,
            skip,
            take,
            where: filterPartnerQuery,
          },
        });

        setPartnerRoles(data.partnerJobs?.items);

        if (tabKey === "partner") {
          partnerPaginationRef.current?.setPagination({
            ...data?.partnerJobs?.pageInfo,
            skip,
            take,
            totalCount: data?.partnerJobs?.totalCount,
          });
        }
      };
      getPartnerRoles();
    } else if (tabKey === "community") {
      const getCommunityRoles = async () => {
        const { data } = await client.query({
          query: GET_COMMUNITY_ROLES,
          variables: {
            search: searchTerm,
            skip,
            take,
            where: filterPublicQuery,
            filterByPreferences: filterIndustry,
          },
        });

        setCommunityRoles(data.communityJobs?.items);
        communityPaginationRef.current?.setPagination({
          ...data?.communityJobs?.pageInfo,
          skip,
          take,
          totalCount: data?.communityJobs?.totalCount,
        });
      };
      getCommunityRoles();
    } else if (tabKey === "project" && boardId !== null) {
      const getProjectRoles = async () => {
        const { data } = await client.query({
          query: GET_PROJECT_ROLES,
          variables: {
            projectJobBoardId: boardId,
            skip,
            take,
            where: filterPartnerQuery,
          },
        });

        setProjectRoles(data.projectJobs?.items);

        projectPaginationRef.current?.setPagination({
          ...data?.projectJobs?.pageInfo,
          skip,
          take,
          totalCount: data?.projectJobs?.totalCount,
        });
      };
      getProjectRoles();
    }
  };

  useEffect(() => {
    setSearchTerm("");
    runSearch();
  }, [tabKey]);

  useEffect(() => {
    if (searchTerm === "" && tabKey !== "project") runSearch();
  }, [searchTerm]);

  useEffect(() => {
    runSearch();
  }, [
    publicRolesData?.publicJobs?.items,
    partnerRolesData?.partnerJobs?.items,
    communityRolesData?.communityJobs?.items,
    boardId,
  ]);

  const selectRole = (ind: number) => {
    if (tabKey === "partner") {
      setActiveRole(partnerRoles[ind]);
      navigate(`/partner-roles/${partnerRoles[ind].id}`);
    } else if (tabKey === "project") {
      setActiveRole(projectRoles[ind]);
      navigate(`/project-roles/${projectRoles[ind].id}`);
    } else if (tabKey === "community") {
      setActiveRole(communityRoles[ind]);
      navigate(`/community-roles/${communityRoles[ind].id}`);
    } else {
      setActiveRole(publicRoles[ind]);
      navigate(`/public-roles/${publicRoles[ind].id}`);
    }
    setShowRoleDetailModal(true);
  };

  const setActiveTab = (tab: string | null) => {
    if (tab) {
      setTabKey(tab);
    }
    setFilterPublicQuery(searchStorage?.publicRolesFilter);
    setFilterPartnerQuery(searchStorage?.partnerRolesFilter);
  };

  const handleOpenAdvancedSearch = () => {
    setShowAdvancedSearch((val) => !val);
  };

  const handleProjectBoardChange = (board: any) => {
    setBoardId(board.value);
  };

  const handleClearSummarySearch = () => {
    if (searchStorage) {
      if (tabKey === "public") {
        searchStorage.publicRolesStatus = false;
        searchStorage.publicRolesSummary = "";
        searchStorage.publicRolesFilter = {};
        setShowSummarySearchPublic(false);
        setFilterPublicQuery({});
      } else if (tabKey === "partner") {
        searchStorage.partnerRolesStatus = false;
        searchStorage.partnerRolesSummary = "";
        searchStorage.partnerRolesFilter = {};
        setShowSummarySearchPartner(false);
        setFilterPartnerQuery({});
      } else if (tabKey === "community") {
        searchStorage.communityRolesStatus = false;
        searchStorage.communityRolesSummary = "";
        searchStorage.communityRolesFilter = {};
        setShowSummarySearchPartner(false);
        setFilterPartnerQuery({});
      // } else if (tabKey === "project") {
      //   searchStorage.projectRolesStatus = false;
      //   searchStorage.projectRolesSummary = "";
      //   searchStorage.projectRolesFilter = {};
      //   setShowSummarySearchProject(false);
      //   setFilterProjectQuery({});
      }
      window.localStorage.setItem(
        "advancedSearchRoles",
        JSON.stringify(searchStorage)
      );
    }
  };

  if (profileLoading) {
    return (
      <PageContainer>
        <div>Loading...</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {showDemoProjectsFlag && <DemoProjects />}
      <PublicRoleDetailModal
        show={showRoleDetailModal}
        role={activeRole}
        onHide={() => {
          let url = `/public-roles`;
          if (tabKey === "partner") {
            url = `/partner-roles`;
          } else if (tabKey === "community") {
            url = `/community-roles`;
          } else if (tabKey === "project") {
            url = `/project-roles`;
          }
          window.history.replaceState(null, "", url);
          setRouteRoleId(null);
          setShowRoleDetailModal(false);
        }}
      />
      <AdvancedSearchContainer>
        {showAdvancedSearch && (
          <AdvancedSearch
            searchType={tabKey}
            isMobile={isMobile}
            onHandleModal={() => setShowAdvancedSearch(false)}
            onHandleSearch={
              tabKey === "public"
                ? (data: PublicJob[]) => setPublicRoles(data)
                : (data: PublicJob[]) => setPartnerRoles(data)
            }
            onShowSummary={
              tabKey === "public"
                ? (data: boolean) => setShowSummarySearchPublic(data)
                : (data: boolean) => setShowSummarySearchPartner(data)
            }
            onHandleSummaryContent={
              tabKey === "public"
                ? (content: string) => setSummarySearchContentPublic(content)
                : (content: string) => setSummarySearchContentPartner(content)
            }
            onHandleFilterQuery={
              tabKey === "public"
                ? (queryData: any) => setFilterPublicQuery(queryData)
                : (queryData: any) => setFilterPartnerQuery(queryData)
            }
          />
        )}
      </AdvancedSearchContainer>
      <PageHeader title="Open Roles" subtitle="Explore opportunities" />
      <Tabs value={tabKey} onValueChange={setActiveTab}>
        <TabsList>
          {!shouldShowOnlyCommunityTabs && (
            <TabsTrigger value="public">Public Roles</TabsTrigger>
          )}
          {!shouldShowOnlyCommunityTabs && communityRoles.length > 0 && (
            <TabsTrigger value="community">Community Roles</TabsTrigger>
          )}
          {partnerRoles.length > 0 && (
            <TabsTrigger value="partner">Partner Roles</TabsTrigger>
          )}
          {projectBoards !== undefined && projectBoards.length > 0 && (
            <TabsTrigger value="project">Project Roles</TabsTrigger>
          )}
        </TabsList>
        {!shouldShowOnlyCommunityTabs && (
          <TabsContent value="public" style={!isMobile ? { position: "relative" } : {}}>
          <StyledTableShell>
            <CardContent>
              <SearchContainer screen={!isMobile}>
                <InputSearchGroup screen={!isMobile}>
                  <Input
                    className={isMobile ? "text-lg" : ""}
                    type="text"
                    value={searchTerm}
                    placeholder="Search by job title, industry or location"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") runSearch();
                    }}
                  />
                  {searchTerm.length > 0 && (
                    <ClearSearchButton
                      size={25}
                      screen={!isMobile}
                      onClick={() => setSearchTerm("")}
                    />
                  )}
                  <Button onClick={() => runSearch()}>
                    <Search className="mr-2 h-4 w-4" /> Search
                  </Button>
                </InputSearchGroup>
                {showAdvancedSearchFlag && (
                  <AdvancedSearchButton
                    screen={!isMobile}
                    onClick={handleOpenAdvancedSearch}
                  >
                    Advanced Search
                  </AdvancedSearchButton>
                )}
              </SearchContainer>
              {isMobile ? <hr /> : ""}
              {showSummarySearchPublic && (
                <p>
                  {summarySearchContentPublic}
                  <ClearSummarySearch onClick={handleClearSummarySearch}>
                    clear search
                  </ClearSummarySearch>
                </p>
              )}
              <DesktopContainer>
                <ToggleIndustryWrapper screen={!isMobile}>
                  <Toggle
                    checked={filterIndustry}
                    icons={false}
                    onChange={() => setFilterIndustry((x) => !x)}
                  />
                  <TextIndustryWrapper>
                    Filter by my{" "}
                    <Link to={"/settings"} state={{ byIndustry: true }}>
                      industry preferences
                    </Link>
                  </TextIndustryWrapper>
                </ToggleIndustryWrapper>
                <StyledTable>
                  <TableHeader>
                    <TableRow>
                      <StyledTableHead>Job Title</StyledTableHead>
                      <StyledTableHead>Industry</StyledTableHead>
                      <StyledTableHead>Location</StyledTableHead>
                      <StyledTableHead>Type</StyledTableHead>
                      <StyledTableHead className="text-center">Status</StyledTableHead>
                      <StyledTableHead>Fee Estimate</StyledTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {publicRoles.length === 0 && (
                      <TableRow>
                        <StyledTableCell colSpan={6}>
                          <EmptyState>No open roles available.</EmptyState>
                        </StyledTableCell>
                      </TableRow>
                    )}
                    {publicRoles.map((role, ind) => (
                      <StyledTableRow key={role.id} onClick={() => selectRole(ind)} style={{ cursor: "pointer" }}>
                        <StyledTableCell>{role.title}</StyledTableCell>
                        <StyledTableCell>{role.industry}</StyledTableCell>
                        <StyledTableCell>{locationName(role?.location?.displayName)}</StyledTableCell>
                        <StyledTableCell>
                          <Badge variant="secondary">
                            {role.workType === "TEMP"
                              ? "CONTRACT/TEMP"
                              : "PERMANENT"}
                          </Badge>
                        </StyledTableCell>
                        <StyledTableCell className="text-center">
                          <StatusBadge status={roleStatusLabels[role?.status] || "Unknown"} />
                        </StyledTableCell>
                        {role.workType === "PERMANENT" ? (
                          <StyledTableCell>
                            {currencyFormat(role.feeMin)} -{" "}
                            {currencyFormat(role.feeMax)} {role?.currency}
                          </StyledTableCell>
                        ) : (
                          <StyledTableCell>{currencyFormat(role.feeMin)} per hr</StyledTableCell>
                        )}
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </DesktopContainer>
              <MobileContainer>
                <ToggleIndustryWrapper screen={!isMobile}>
                  <Toggle
                    checked={filterIndustry}
                    icons={false}
                    onChange={() => setFilterIndustry((x) => !x)}
                  />
                  <TextIndustryWrapper>
                    Filter by my{" "}
                    <Link to={"/settings"} state={{ byIndustry: true }}>
                      industry preferences
                    </Link>
                  </TextIndustryWrapper>
                </ToggleIndustryWrapper>
                {publicRoles.map((role, ind) => (
                  <MobileCard key={role.id} onClick={() => selectRole(ind)}>
                    <div>
                      <h4>{role.title}</h4>
                      <strong>Industry:</strong> {role.industry}
                      <br />
                      <strong>Location:</strong>{" "}
                      {locationName(role?.location?.displayName)}
                      <br />
                    </div>
                    <div>
                      <Badge variant="secondary">
                        {role.workType === "TEMP"
                          ? "CONTRACT/TEMP"
                          : "PERMANENT"}
                      </Badge>
                      {role.workType === "PERMANENT" ? (
                        <div>
                          {currencyFormat(role.feeMin)} -{" "}
                          {currencyFormat(role.feeMax)}
                        </div>
                      ) : (
                        <div>{currencyFormat(role.feeMin)} per hr</div>
                      )}
                    </div>
                  </MobileCard>
                ))}
              </MobileContainer>
              {hasNextPagePublic && (
                <CustomPagination
                  ref={publicPaginationRef}
                  onPaginationChange={(args) => runSearch(args.skip, args.take)}
                />
              )}
              <p>
                <a href={guide} target="_blank" rel="noreferrer">
                  Click here to view the User Guide.
                </a>
              </p>
            </CardContent>
          </StyledTableShell>
        </TabsContent>
        )}
        {(shouldShowOnlyCommunityTabs || communityRoles.length > 0) && (
          <TabsContent value="community" style={!isMobile ? { position: "relative" } : {}}>
            <StyledTableShell>
              <CardContent>
                <DesktopContainer>
                  <StyledTable>
                    <TableHeader>
                      <TableRow>
                        <StyledTableHead>Job Title</StyledTableHead>
                        <StyledTableHead>Industry</StyledTableHead>
                        <StyledTableHead>Location</StyledTableHead>
                        <StyledTableHead>Type</StyledTableHead>
                        <StyledTableHead>Agency</StyledTableHead>
                        <StyledTableHead>Fee Estimate</StyledTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {communityRoles.map((role, ind) => {
                        
                        const isAnonymous = !role.ownerCompanyName;

                        return (
                          <StyledTableRow key={role.id} onClick={() => selectRole(ind)} style={{ cursor: "pointer" }}>
                            <StyledTableCell>{role.title}</StyledTableCell>
                            <StyledTableCell>{role.industry}</StyledTableCell>
                            <StyledTableCell>{locationName(role?.location?.displayName)}</StyledTableCell>
                            <StyledTableCell>
                              <Badge variant="secondary">
                                {role.workType === "TEMP" ? "CONTRACT/TEMP" : "PERMANENT"}
                              </Badge>
                            </StyledTableCell>
                            
                          
                            <StyledTableCell>
                              {isAnonymous ? (
                                <span className="flex items-center gap-1 text-slate-500 italic">
                                    <LockIcon className="w-3 h-3" /> Confidential
                                </span>
                              ) : (
                                <span className="font-medium text-teal-600">
                                    {role.ownerCompanyName}
                                </span>
                              )}
                            </StyledTableCell>

                            {role.workType === "PERMANENT" ? (
                              <StyledTableCell>
                                {currencyFormat(role.feeMin)} - {currencyFormat(role.feeMax)} {role?.currency}
                              </StyledTableCell>
                            ) : (
                              <StyledTableCell>{currencyFormat(role.feeMin)} per hr</StyledTableCell>
                            )}
                          </StyledTableRow>
                        );
                      })}
                      {communityRoles.length === 0 && (
                        <TableRow>
                          <StyledTableCell colSpan={6}>
                            <EmptyState>No open roles available.</EmptyState>
                          </StyledTableCell>
                        </TableRow>
                      )}
                     
                    </TableBody>
                  </StyledTable>
                </DesktopContainer>
                <MobileContainer>
                  <ToggleIndustryWrapper screen={!isMobile}>
                    <Toggle
                      checked={filterIndustry}
                      icons={false}
                      onChange={() => setFilterIndustry((x) => !x)}
                    />
                    <TextIndustryWrapper>
                      Filter by my{" "}
                      <Link to={"/settings"} state={{ byIndustry: true }}>
                        industry preferences
                      </Link>
                    </TextIndustryWrapper>
                  </ToggleIndustryWrapper>
                  {communityRoles.map((role, ind) => {
                    // 🚀 THE MAGIC CHECK
                    const isAnonymous = !role.ownerCompanyName;

                    return (
                      <MobileCard key={role.id} onClick={() => selectRole(ind)}>
                        <div>
                          <h4>{role.title}</h4>
                          <strong>Industry:</strong> {role.industry}
                          <br />
                          <strong>Location:</strong> {locationName(role?.location?.displayName)}
                          <br />
                          {/* added privacy rebdering*/}
                          <strong>Agency:</strong> {isAnonymous ? (
                            <span className="text-slate-500 italic">Confidential</span>
                          ) : (
                            <span className="text-teal-600">{role.ownerCompanyName}</span>
                          )}
                        </div>
                        <div className="mt-2 flex justify-between items-center">
                          <Badge variant="secondary">
                            {role.workType === "TEMP" ? "CONTRACT/TEMP" : "PERMANENT"}
                          </Badge>
                          <div className="font-bold text-primary">
                            {role.workType === "PERMANENT" 
                              ? `${currencyFormat(role.feeMin)} - ${currencyFormat(role.feeMax)}` 
                              : `${currencyFormat(role.feeMin)} per hr`}
                          </div>
                        </div>
                      </MobileCard>
                    );
                  })}
                 
                </MobileContainer>
                {hasNextPageCommunity && (
                  <CustomPagination
                    ref={communityPaginationRef}
                    onPaginationChange={(args) =>
                      runSearch(args.skip, args.take)
                    }
                  />
                )}
                <p>
                  <a href={guide} target="_blank" rel="noreferrer">
                    Click here to view the User Guide.
                  </a>
                </p>
              </CardContent>
            </StyledTableShell>
          </TabsContent>
        )}
        {partnerRoles.length > 0 && (
          <TabsContent value="partner" style={!isMobile ? { position: "relative" } : {}}>
            <StyledTableShell>
              <CardContent>
                <SearchContainer screen={!isMobile}>
                  <InputSearchGroup screen={!isMobile}>
                    <Input
                      className={isMobile ? "text-lg" : ""}
                      type="text"
                      value={searchTerm}
                      placeholder="Search by job title, industry or location.."
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") runSearch();
                      }}
                    />
                    {searchTerm.length > 0 && (
                      <ClearSearchButton
                        size={25}
                        screen={!isMobile}
                        onClick={() => setSearchTerm("")}
                      />
                    )}
                    <Button onClick={() => runSearch()}>
                      <Search className="mr-2 h-4 w-4" /> Search
                    </Button>
                  </InputSearchGroup>
                  {showAdvancedSearchFlag && (
                    <AdvancedSearchButton
                      screen={!isMobile}
                      onClick={handleOpenAdvancedSearch}
                    >
                      Advanced Search
                    </AdvancedSearchButton>
                  )}
                </SearchContainer>
                {isMobile ? <hr /> : ""}
                {showSummarySearchPartner && (
                  <p>
                    {summarySearchContentPartner}
                    <ClearSummarySearch onClick={handleClearSummarySearch}>
                      clear search
                    </ClearSummarySearch>
                  </p>
                )}
                <DesktopContainer>
                  <StyledTable>
                    <TableHeader>
                      <TableRow>
                        <StyledTableHead>Job Title</StyledTableHead>
                        <StyledTableHead>Industry</StyledTableHead>
                        <StyledTableHead>Location</StyledTableHead>
                        <StyledTableHead>Type</StyledTableHead>
                        <StyledTableHead className="text-center">Status</StyledTableHead>
                        <StyledTableHead>Agency</StyledTableHead>
                        <StyledTableHead>Fee Estimate</StyledTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partnerRoles.length === 0 && (
                        <TableRow>
                          <StyledTableCell colSpan={7}>
                            <EmptyState>No partner roles available.</EmptyState>
                          </StyledTableCell>
                        </TableRow>
                      )}
                      {partnerRoles.map((role, ind) => (
                        <StyledTableRow key={role.id} onClick={() => selectRole(ind)} style={{ cursor: "pointer" }}>
                          <StyledTableCell>{role.title}</StyledTableCell>
                          <StyledTableCell>{role.industry}</StyledTableCell>
                          <StyledTableCell>{locationName(role?.location?.displayName)}</StyledTableCell>
                          <StyledTableCell>
                            <Badge variant="secondary">
                              {role.workType === "TEMP"
                                ? "CONTRACT/TEMP"
                                : "PERMANENT"}
                            </Badge>
                          </StyledTableCell>
                          <StyledTableCell className="text-center">
                            <StatusBadge status={roleStatusLabels[role?.status] || "Unknown"} />
                          </StyledTableCell>
                          <StyledTableCell>{role.companyName}</StyledTableCell>
                          <StyledTableCell>
                            {currencyFormat(role.feeMin)} -{" "}
                            {currencyFormat(role.feeMax)} {role?.currency}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </DesktopContainer>
                <MobileContainer>
                  {partnerRoles.map((role, ind) => (
                    <MobileCard key={role.id} onClick={() => selectRole(ind)}>
                      <div>
                        <h4>{role.title}</h4>
                        <strong>Industry:</strong> {role.industry}
                        <br />
                        <strong>Location:</strong>{" "}
                        {locationName(role?.location?.displayName)}
                        <br />
                        <strong>Agency:</strong> {role.companyName}
                        <br />
                      </div>
                      <div>
                        <Badge variant="secondary">
                          {role.workType === "TEMP"
                            ? "CONTRACT/TEMP"
                            : "PERMANENT"}
                        </Badge>
                        {role.workType === "PERMANENT" ? (
                          <div>
                            {currencyFormat(role.feeMin)} -{" "}
                            {currencyFormat(role.feeMax)}
                          </div>
                        ) : (
                          <div>{currencyFormat(role.feeMin)} per hr</div>
                        )}
                      </div>
                    </MobileCard>
                  ))}
                </MobileContainer>
                {hasNextPagePartner && (
                  <CustomPagination
                    ref={partnerPaginationRef}
                    onPaginationChange={(args) =>
                      runSearch(args.skip, args.take)
                    }
                  />
                )}
                <p>
                  <a href={guide} target="_blank" rel="noreferrer">
                    Click here to view the User Guide.
                  </a>
                </p>
              </CardContent>
            </StyledTableShell>
          </TabsContent>
        )}
        {projectBoards !== undefined && projectBoards.length > 0 && (
          <TabsContent value="project" style={!isMobile ? { position: "relative" } : {}}>
            <StyledTableShell>
              <CardContent>
                <div className="mb-3">
                  {loadingProjectBoards && <span>Loading...</span>}
                  {!loadingProjectBoards &&
                    !errorProjectBoards &&
                    projectBoards?.length && (
                      <ReactSelect
                        options={boardOptions}
                        onChange={handleProjectBoardChange}
                        defaultValue={boardOptions.filter(
                          (option: any) => option.value === boardId
                        )}
                      />
                      // <Dropdown>
                      //   <Dropdown.Toggle variant="primary" id="dropdown-basic">
                      //     Select Projects
                      //   </Dropdown.Toggle>
                      //   <Dropdown.Menu>
                      //     {projectBoards?.map(({id, title}) => (
                      //       <Dropdown.Item
                      //         key={id}
                      //         href="#"
                      //       >
                      //         {title}
                      //       </Dropdown.Item>
                      //     ))}
                      //   </Dropdown.Menu>
                      // </Dropdown>
                    )}
                </div>
                <DesktopContainer>
                  <StyledTable>
                    <TableHeader>
                      <TableRow>
                        <StyledTableHead>Job Title</StyledTableHead>
                        <StyledTableHead>Industry</StyledTableHead>
                        <StyledTableHead>Location</StyledTableHead>
                        <StyledTableHead>Type</StyledTableHead>
                        <StyledTableHead className="text-center">Status</StyledTableHead>
                        <StyledTableHead>Agency</StyledTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectRoles.length === 0 && (
                        <TableRow>
                          <StyledTableCell colSpan={6}>
                            <EmptyState>No project roles available.</EmptyState>
                          </StyledTableCell>
                        </TableRow>
                      )}
                      {projectRoles.map((role, ind) => (
                        <StyledTableRow key={role?.id} onClick={() => selectRole(ind)} style={{ cursor: "pointer" }}>
                          <StyledTableCell>{role?.title}</StyledTableCell>
                          <StyledTableCell>{role?.industry}</StyledTableCell>
                          <StyledTableCell>{locationName(role?.location?.displayName)}</StyledTableCell>
                          <StyledTableCell>
                            <Badge variant="secondary">
                              {role?.workType === "TEMP"
                                ? "CONTRACT/TEMP"
                                : "PERMANENT"}
                            </Badge>
                          </StyledTableCell>
                          <StyledTableCell className="text-center">
                            <StatusBadge status={roleStatusLabels[role?.status] || "Unknown"} />
                          </StyledTableCell>
                          <StyledTableCell>{role?.companyName}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </DesktopContainer>
                <MobileContainer>
                  {partnerRoles.map((role, ind) => (
                    <MobileCard key={role?.id} onClick={() => selectRole(ind)}>
                      <div>
                        <h4>{role?.title}</h4>
                        <strong>Industry:</strong> {role?.industry}
                        <br />
                        <strong>Location:</strong>{" "}
                        {locationName(role?.location?.displayName)}
                        <br />
                        <strong>Agency:</strong> {role?.companyName}
                        <br />
                      </div>
                      <div>
                        <Badge variant="secondary">
                          {role?.workType === "TEMP"
                            ? "CONTRACT/TEMP"
                            : "PERMANENT"}
                        </Badge>
                        {role?.workType === "PERMANENT" ? (
                          <div>
                            {currencyFormat(role?.feeMin)} -{" "}
                            {currencyFormat(role?.feeMax)}
                          </div>
                        ) : (
                          <div>{currencyFormat(role?.feeMin)} per hr</div>
                        )}
                      </div>
                    </MobileCard>
                  ))}
                </MobileContainer>
              </CardContent>
            </StyledTableShell>
          </TabsContent>
        )}
      </Tabs>
    </PageContainer>
  );
};

export default PublicRoles;
